import HttpService from '@src/utility/httpService';
import { checkIsInternal, getPSMSProviderEntityId } from '@src/utility/createStorage';
import { config } from '@uhg-abyss/web/tools/config';

const _httpService = new HttpService();

if (checkIsInternal()) {
  _httpService.setBaseURL(config('API_DOMAIN_INTERNAL'));
} else {
  _httpService.setBaseURL(config('API_DOMAIN_EXTERNAL'));
}

/**
 * Fetches recovery rates trend metrics data
 * Endpoint: /recovery-rates-trend-metrics/
 * Method: POST
 *
 * @param payload - Request payload containing:
 *   - TimeFilter: e.g., "CalendarYear"
 *   - TimeFilterText: e.g., "2024"
 *   - Region: Optional region filter
 *   - State: Optional state filter(s)
 *   - Market: Optional market filter(s)
 *   - GeoMapping: Optional geo mapping
 *   - ProviderSysKey: Optional provider system key
 *   - ClaimsBy: Optional claims by filter
 *   - OverturnPass: Optional overturn pass filter
 *   - appealsProcessing: Optional appeals processing filter
 *
 * @returns Promise with API response containing:
 *   - Status: "MULTI_STATUS"
 *   - StatusCode: 207
 *   - Data: Object with Startdate, Enddate, and LOB data (All, Cs, Ei, etc.)
 *     Each LOB contains:
 *       - MonthlyRecoveryRates: Array of monthly data with Month, Recouped, Paid, RecoveryPercent
 *       - OverallRecoveryPercent: Overall recovery percentage
 *       - TotalRecouped: Total recouped amount
 *       - TotalPaid: Total paid amount
 */
export const getRecoveryRatesTrendMetrics = (payload: any) => {
  const stored = sessionStorage.getItem('psmsState');
  const storedPayload = stored ? JSON.parse(stored) : {};
  const currentFlow = storedPayload?.currentFlow || 'HCO'; // Default to HCO if not set

  const updatedPayload = {
    ...payload,
    IsPSMS: '1',
    ClaimsBy: 'DOS',
    OverturnPass: 'All',
    appealsProcessing: 'Received Date',
  };
  if (currentFlow === 'HCO') {
    updatedPayload.EntityId = getPSMSProviderEntityId();
  }
  if (updatedPayload.Tins) {
    updatedPayload.Tin = updatedPayload.Tins;
    delete updatedPayload.Tins;
  }
  const fetchData = async () => {
    try {
      const response: any = await _httpService.push(
        'recovery-rates-trend-metrics/',
        updatedPayload
      );
      return response;
    } catch (error) {
      console.error('Error fetching recovery rates trend metrics:', error);
      return null;
    }
  };
  return fetchData();
};

/**
 * Fetches settlements details data
 * Endpoint: /settlements/settlements-details
 * Method: POST
 *
 * Makes 3 parallel API calls:
 * 1. Total settlements (no type field)
 * 2. Open settlements (type: "open")
 * 3. Closed settlements (type: "close")
 *
 * @param payload - Base request payload containing:
 *   - TimeFilter: e.g., "CalendarYear"
 *   - TimeFilterText: e.g., "2024"
 *   - Region: Optional region filter
 *   - State: Optional state filter(s)
 *   - Market: Optional market filter(s)
 *   - GeoMapping: Optional geo mapping
 *   - EntityId: For HCO flow (instead of ProviderSysKey)
 *   - IsPSMS: "1" for PSMS flow
 *   - And other filters...
 *
 * @returns Promise with combined response containing:
 *   - total: { Data: { Details: [...] }, startDate, endDate }
 *   - open: { Data: { Details: [...] }, startDate, endDate }
 *   - closed: { Data: { Details: [...] }, startDate, endDate }
 *   - error: Error information if any call fails
 *
 * Each Details array contains:
 *   - BusinessID
 *   - TotalSettlement
 *   - ProviderDemand
 *   - TotalRecoveryBalance
 *   - LineOfBusiness
 *   - SubmitterName
 *   - Type
 *   - TaxID
 *   - TinName
 *   - PartOfMultiTin
 */
export const getSettlementsDetails = async (payload: any) => {
  const updatedPayload = {
    ...payload,
    IsPSMS: '1',
  };
  if (updatedPayload.Tins) {
    updatedPayload.Tin = updatedPayload.Tins;
    delete updatedPayload.Tins;
  }
  // delete updatedPayload.EntityId;
  try {
    // Create payloads for each type
    const totalPayload = { ...updatedPayload }; // No Type field for total
    const openPayload = { ...updatedPayload, Type: 'open' };
    const closedPayload = { ...updatedPayload, Type: 'closed' };

    // Make all 3 API calls in parallel
    const [totalResponse, openResponse, closedResponse] = await Promise.all([
      _httpService.push('settlements/settlements-details', totalPayload),
      _httpService.push('settlements/settlements-details', openPayload),
      _httpService.push('settlements/settlements-details', closedPayload),
    ]);

    // Extract data from responses
    const extractData = (response: any) => {
      if (response?.Data) {
        return {
          Details: response.Data.Details || [],
          startDate: response.Data.Startdate || response.Data.StartDate,
          endDate: response.Data.Enddate || response.Data.EndDate,
        };
      }
      return { Details: [], startDate: null, endDate: null };
    };

    return {
      total: extractData(totalResponse),
      open: extractData(openResponse),
      closed: extractData(closedResponse),
      error: null,
    };
  } catch (error) {
    console.error('Error fetching settlements details:', error);
    return {
      total: { Details: [], startDate: null, endDate: null },
      open: { Details: [], startDate: null, endDate: null },
      closed: { Details: [], startDate: null, endDate: null },
      error: error,
    };
  }
};

export const getARSummary = async (payload: any) => {
  const stored = sessionStorage.getItem('psmsState');
  const storedPayload = stored ? JSON.parse(stored) : {};
  const currentFlow = storedPayload?.currentFlow || 'HCO'; // Default to HCO if not set

  const updatedPayload = {
    ...payload,
    IsPSMS: '1',
    ClaimsBy: 'DOS',
    OverturnPass: 'All',
    appealsProcessing: 'Received Date',
    Type: 'Volume',
  };
  if (updatedPayload.Tins) {
    updatedPayload.Tin = updatedPayload.Tins;
    delete updatedPayload.Tins;
  }
  if (currentFlow === 'HCO') {
    updatedPayload.EntityId = getPSMSProviderEntityId();
  }
  try {
    const [claimsOverturnResponse, paymentMetricsResponse] = await Promise.all([
      _httpService.push('claims-overturn-metrics', updatedPayload),
      _httpService.push('claim-metrics/?request-type=PAYMENT_METRICS', updatedPayload),
    ]);

    return {
      claimsOverturnData: claimsOverturnResponse?.Data || null,
      paymentMetricsData: paymentMetricsResponse?.Data || null,
      error: null,
    };
  } catch (error) {
    console.error('Error fetching AR summary:', error);
    return { claimsOverturnData: null, paymentMetricsData: null, error };
  }
};
